import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/** Backstage error bodies are `{ error: { name, message } }`; anything else is
 * shown as the status alone rather than as unreadable JSON. */
export async function errorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const message = body?.error?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  } catch {
    // Not JSON — fall through to the status.
  }
  return `Request failed with status ${response.status}.`;
}

type Read<T> =
  | { kind: 'loading' }
  | { kind: 'error'; error: Error }
  | { kind: 'loaded'; response: T };

export type ReleaseReadState<T> = Read<T> & {
  /** Reads again, past the backend's cache. The overlays are cached for
   * minutes, so after merging a pull request the screen would otherwise show
   * the old version with nothing to do but wait an unknown length of time.
   *
   * Does nothing while `cooling`. */
  refresh: () => void;
  /** How many times the operator has asked for a fresh read. Passed on to
   * anything with a cache of its own — the dialog derives its plan from the
   * repository tags, which are cached separately, and after pushing a tag from
   * this very portal those are exactly what has gone stale. */
  generation: number;
  /** True for a short while after a refresh. Going past the cache means
   * pulling the whole gitops tree from GitHub, so a button anyone can hold
   * down is a rate limit waiting to happen. */
  cooling: boolean;
};

/**
 * A minute, against a cache that holds for five.
 *
 * Going past the cache pulls the whole gitops tree from GitHub, so this is the
 * one control on these screens that costs real requests. A minute still lets
 * someone check back a few times after merging a pull request, and makes
 * holding the button down pointless.
 */
const COOLDOWN_MS = 60_000;

/**
 * One read from the `release` backend, with the refresh discipline every
 * screen shares.
 *
 * The deployments card, the releases tab and the configuration tab all read
 * from the same cached tree, so they share the same cooldown rules; only the
 * route, the query and the shape of the answer differ.
 */
export function useReleaseRead<T>(options: {
  route: string;
  search: string;
  /** Rejects a body that is not the shape expected, so a backend that has
   * drifted shows as an error rather than as a crash further down. */
  isResponse: (body: unknown) => body is T;
  /** Named in that error: "Deployments response was not in the expected shape." */
  shape: string;
}): ReleaseReadState<T> {
  const { route, search, isResponse, shape } = options;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<Read<T>>({ kind: 'loading' });
  const [asked, setAsked] = useState(0);
  const [cooling, setCooling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const load = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('release');
      const response = await fetchApi.fetch(
        `${baseUrl}/${route}?${search}${asked > 0 ? '&refresh=true' : ''}`,
      );
      if (!response.ok) {
        // Surface what the backend said. A bare status code sends whoever hits
        // this reading the wrong code — a 404 here usually means the portal
        // cannot see gitops-platform, not that the service has no deployments.
        throw new Error(await errorMessage(response));
      }
      const body = await response.json();
      if (!isResponse(body)) {
        throw new Error(`${shape} response was not in the expected shape.`);
      }
      if (!cancelled) {
        setState({ kind: 'loaded', response: body });
      }
    };
    load().catch(e => {
      if (!cancelled) {
        setState({ kind: 'error', error: e as Error });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, route, search, isResponse, shape, asked]);

  useEffect(() => {
    if (!cooling) {
      return undefined;
    }
    const timer = setTimeout(() => setCooling(false), COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [cooling]);

  return {
    ...state,
    generation: asked,
    cooling,
    refresh: () => {
      if (cooling) {
        return;
      }
      setCooling(true);
      setAsked(n => n + 1);
    },
  };
}
